import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Clock, ShieldCheck, Users } from "lucide-react";
import { COMPANY, SERVICES } from "@/lib/site";
import Reveal from "@/components/Reveal";

const HERO_BG = "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBJVCUyMHN1cHBvcnQlMjBuZXR3b3JrJTIwc2VydmVyJTIwcm9vbXxlbnwwfHx8fDE3ODM0NjU1MDN8MA&ixlib=rb-4.1.0&q=85";
const LOCATION_IMG = "https://images.unsplash.com/photo-1655183988041-967ad2b1ea9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxCYXRvbiUyMHJvdWdlJTIwbG91aXNpYW5hJTIwY2l0eXNjYXBlfGVufDB8fHx8MTc4MzQ2NTUwM3ww&ixlib=rb-4.1.0&q=85";

const WHY = [
  { icon: MapPin, title: "Local to Baton Rouge", text: "On-site support across the greater Baton Rouge area — we know your community." },
  { icon: Clock, title: "Responsive Service", text: "Fast turnaround when something breaks, plus proactive maintenance so it doesn't." },
  { icon: ShieldCheck, title: "Security First", text: "Firewalls, surveillance, and secure networks that keep your business protected." },
  { icon: Users, title: "Business Focused", text: "We support businesses, not just devices — solutions that fit how you operate." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Server room" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1A110B]/75" />
          <div className="absolute inset-0 grain-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
          <div className="max-w-3xl">
            <span className="inline-block text-xs uppercase tracking-[0.25em] font-bold text-[#FFA500] mb-6">
              Baton Rouge, Louisiana
            </span>
            <h1 className="font-heading text-5xl md:text-7xl leading-[0.95] tracking-tight text-white">
              <span className="font-light">IT Services &</span><br />
              <span className="font-black">Support that keeps</span><br />
              <span className="font-black text-[#00B4C4]">businesses running.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#E7DED8] max-w-2xl leading-relaxed">
              Telephone Systems, IT Sales, Service, Installation and Support for businesses in and
              around Baton Rouge! ~Firewalls- Network Gear- Enterprise/Facility WiFi- Video
              Surveillance- Full on-premise Computer/Server Support~
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-contact-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#FFA500] text-[#1A110B] font-heading font-black tracking-wider uppercase text-sm px-8 py-4 rounded-sm hover:-translate-y-0.5 hover:shadow-xl transition-all"
              >
                Get a Free Consultation <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-cta"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white font-heading font-bold tracking-wider uppercase text-sm px-8 py-4 rounded-sm hover:bg-white hover:text-[#1A110B] transition-all"
              >
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal className="max-w-2xl mb-16">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#8B4513]">What we do</span>
          <h2 className="font-heading text-3xl md:text-5xl tracking-tight mt-4">
            <span className="font-light">Complete IT support,</span>{" "}
            <span className="font-black">one local partner.</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#E5E1DE]">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`home-service-${s.slug}`}
                  className="group relative border-r border-b border-[#E5E1DE] p-8 md:p-10 h-full hover:bg-white transition-colors"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-[#00B4C4]/10 text-[#00B4C4] group-hover:bg-[#00B4C4] group-hover:text-white transition-colors">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-heading font-bold text-xl mt-6">{s.title}</h3>
                  <p className="mt-3 text-[#594A42] leading-relaxed text-sm">{s.short}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white border-y border-[#E5E1DE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal className="max-w-2xl mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#8B4513]">Why Beta-Tech</span>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight mt-4 font-black">
              Support you can count on.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} delay={i * 0.08}>
                  <div className="border-t-2 border-[#00B4C4] pt-6">
                    <Icon className="h-7 w-7 text-[#8B4513]" />
                    <h3 className="font-heading font-bold text-lg mt-4">{w.title}</h3>
                    <p className="mt-3 text-sm text-[#594A42] leading-relaxed">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <img src={LOCATION_IMG} alt="Baton Rouge Louisiana" className="w-full h-[420px] object-cover rounded-sm" />
              <div className="absolute -bottom-5 -right-5 hidden md:block bg-[#FFA500] text-[#1A110B] px-6 py-4 rounded-sm">
                <span className="font-heading font-black text-2xl">Baton Rouge</span>
                <span className="block text-xs uppercase tracking-widest font-bold">& surrounding areas</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#8B4513]">Service Area</span>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight mt-4">
              <span className="font-light">Proudly serving</span>{" "}
              <span className="font-black">local businesses.</span>
            </h2>
            <p className="mt-6 text-[#594A42] text-lg leading-relaxed">
              Beta-Tech Systems provides on-site and remote IT support to businesses throughout
              Baton Rouge and the surrounding Louisiana communities. When you need hands-on help,
              a local team is close by.
            </p>
            <Link
              to="/contact"
              data-testid="service-area-cta"
              className="mt-8 inline-flex items-center gap-2 text-[#1A110B] font-heading font-bold uppercase tracking-wider text-sm border-b-2 border-[#FFA500] pb-1 hover:text-[#00B4C4] transition-colors"
            >
              Talk to us today <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="bg-[#1A110B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-heading text-3xl md:text-4xl font-black text-white text-center md:text-left">
            Ready to get your IT handled?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              data-testid="cta-banner-contact"
              className="inline-flex items-center justify-center gap-2 bg-[#FFA500] text-[#1A110B] font-heading font-black tracking-wider uppercase text-sm px-8 py-4 rounded-sm hover:-translate-y-0.5 transition-transform"
            >
              Contact Us
            </Link>
            <a
              href={COMPANY.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-banner-payment"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white font-heading font-bold tracking-wider uppercase text-sm px-8 py-4 rounded-sm hover:bg-white hover:text-[#1A110B] transition-all"
            >
              Make a Payment
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
